// src/stores/JobNotesStore.js - Zustand Store replacing JobNotesSocketService + JobNotesProvider
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';
import FileUploadService from '../services/files/FileUploadService';

// Store creation with selector subscription middleware
const useJobNotesStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (mirrors JobNotesProvider exactly)
    // ================================
    
    // Main job notes data (organized by event)
    notesByEvent: {}, // { eventId: [notes] }
    selectedNote: null,
    recentNotes: [],
    
    // Loading states (granular like provider)
    notesLoading: false,
    createNoteLoading: false,
    updateNoteLoading: false,
    deleteNoteLoading: false,
    
    // File upload states
    fileUploading: false,
    multipleFileUploading: false,
    fileDeleting: false,
    
    // File operation tracking
    uploadProgress: null, // { current: 1, total: 3 }

    // Connection state
    isConnected: false,
    
    // Cache state (from JobNotesSocketService)
    cache: {
      notesByEvent: {},
      selectedNote: null,
      recentNotes: [],
      lastFetch: null
    },
    
    // Internal flags
    _initialized: false,
    
    // ================================
    // SOCKET OPERATIONS (from JobNotesSocketService)
    // ================================
    
    /**
     * Request job notes for a specific event - NO RETURN VALUE
     * @param {string} eventId - Event ID to load notes for
     * @param {string} noteType - Optional filter ('internal', 'external', or null for all)
     */
    loadEventNotes: (eventId, noteType = null) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ JobNotesStore: Socket service not initialized');
        return;
      }
      
      if (!eventId) {
        console.error('❌ JobNotesStore: Event ID is required');
        return;
      }
      
      console.log('📝 JobNotesStore: Loading notes for event:', eventId, noteType ? `(${noteType} only)` : '(all types)');
      
      set({
        notesLoading: true
      });
      
      const requestData = { event_id: eventId };
      if (noteType) {
        requestData.note_type = noteType;
      }
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_JOB_NOTES, requestData);
    },
    
    /**
     * Request to create a new job note - NO RETURN VALUE
     * @param {string} eventId - Event ID to create note for
     * @param {string} noteType - Type of note ('internal' or 'external')
     * @param {string} content - Note content
     */
    createNote: (eventId, noteType, content) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ JobNotesStore: Socket service not initialized');
        return;
      }
      
      // Validate required fields
      if (!eventId || !noteType || !content.trim()) {
        console.error('❌ JobNotesStore: Event ID, note type, and content are required');
        return;
      }

      // Validate note type
      if (!['internal', 'external'].includes(noteType)) {
        console.error('❌ JobNotesStore: Note type must be "internal" or "external"');
        return;
      }
      
      console.log('📝 JobNotesStore: Creating note:', { eventId, noteType, contentLength: content.length });
      
      set({
        createNoteLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_JOB_NOTE, {
        event_id: eventId,
        note_type: noteType,
        content: content.trim()
      });
    },
    
    /**
     * Request to update an existing job note - NO RETURN VALUE
     * @param {string} noteId - Note ID to update
     * @param {string} content - New note content
     */
    updateNote: (noteId, content) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ JobNotesStore: Socket service not initialized');
        return;
      }
      
      // Validate required fields
      if (!noteId || !content.trim()) {
        console.error('❌ JobNotesStore: Note ID and content are required');
        return;
      }
      
      console.log('📝 JobNotesStore: Updating note:', noteId);
      
      set({
        updateNoteLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_JOB_NOTE, {
        note_id: noteId,
        content: content.trim()
      });
    },
    
    /**
     * Request to delete a job note - NO RETURN VALUE
     * @param {string} noteId - Note ID to delete
     */
    deleteNote: (noteId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ JobNotesStore: Socket service not initialized');
        return;
      }
      
      // Validate required fields
      if (!noteId) {
        console.error('❌ JobNotesStore: Note ID is required');
        return;
      }
      
      console.log('📝 JobNotesStore: Deleting note:', noteId);
      
      set({
        deleteNoteLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_JOB_NOTE, {
        note_id: noteId
      });
    },
    
    // ================================
    // PROMISE-BASED COORDINATION METHODS (from JobNotesSocketService)
    // ================================
    
    /**
     * Create note and wait for WebSocket response - RETURNS PROMISE
     * This is the foundation for createNoteWithPhotos()
     */
    _createNoteAndWait: (eventId, noteType, content) => {
      console.log('📝 JobNotesStore: Creating note and waiting for response');
      
      return new Promise((resolve, reject) => {
        let isResolved = false;
        
        // Set up one-time listener for success
        const cleanup = get()._setupOneTimeNoteCreatedListener((noteData) => {
          // Verify this is the note we just created
          if (noteData.event_id === eventId && !isResolved) {
            isResolved = true;
            cleanup();
            console.log('✅ JobNotesStore: Note created successfully:', noteData.id);
            resolve(noteData);
          }
        });
        
        // Set up timeout
        const timeoutId = setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            console.error('❌ JobNotesStore: Note creation timeout');
            reject(new Error('Note creation timeout - WebSocket response not received'));
          }
        }, 10000); // 10 second timeout
        
        try {
          // Emit the creation request
          get().createNote(eventId, noteType, content);
          console.log('📝 JobNotesStore: Waiting for note creation response...');
        } catch (error) {
          clearTimeout(timeoutId);
          cleanup();
          reject(error);
        }
      });
    },
    
    /**
     * Create note with photos - RETURNS PROMISE
     * This is the main method that coordinates WebSocket + HTTP operations
     */
    createNoteWithPhotos: async (eventId, noteType, content, files = [], captions = []) => {
      console.log('📝 JobNotesStore: Creating note with photos:', {
        eventId, noteType, contentLength: content?.length, filesCount: files?.length
      });
      
      try {
        set({
          createNoteLoading: true,
          fileUploading: files.length > 0
        });
        
        // Step 1: Create note via WebSocket and wait for response
        console.log('📝 JobNotesStore: Step 1 - Creating note via WebSocket...');
        const noteData = await get()._createNoteAndWait(eventId, noteType, content);
        
        // Step 2: Upload files if any
        if (files && files.length > 0) {
          console.log('📁 JobNotesStore: Step 2 - Uploading', files.length, 'files to note:', noteData.id);
          
          const uploadResult = await FileUploadService.uploadMultiplePhotos(
            noteData.id, 
            eventId, 
            files, 
            captions
          );

          if (!uploadResult.success) {
            console.error('❌ JobNotesStore: File upload failed:', uploadResult.error);
            console.error('❌ JobNotesStore: Note created but file upload failed:', uploadResult.error);
            throw new Error(`Note created but file upload failed: ${uploadResult.error}`);
          }

          if (uploadResult.data?.failed_uploads > 0) {
            console.warn('⚠️ JobNotesStore: Some files failed to upload:', uploadResult.data.errors);
            const successCount = uploadResult.data.successful_uploads;
            const failCount = uploadResult.data.failed_uploads;
            console.warn('⚠️ JobNotesStore: Partial upload success:', successCount, 'succeeded,', failCount, 'failed');
          }

          // Step 3: Refresh notes to get updated data with photos
          console.log('🔄 JobNotesStore: Step 3 - Refreshing notes to include uploaded files...');
          get().loadEventNotes(eventId);
        }

        console.log('✅ JobNotesStore: createNoteWithPhotos completed successfully');
        return noteData;

      } catch (error) {
        console.error('❌ JobNotesStore: createNoteWithPhotos failed:', error.message);
        throw error;
      } finally{
        set({ 
          createNoteLoading: false,
          fileUploading: false 
        });
      }
    },
    
    // ================================
    // FILE OPERATIONS (from JobNotesProvider)
    // ================================
    
    /**
     * Upload photo to existing note
     */
    uploadPhotoToNote: async (noteId, eventId, file, caption = '') => {
      console.log('📁 JobNotesStore: Uploading photo to note:', noteId);
      
      try {
        set({
          fileUploading: true
        });
        
        await FileUploadService.uploadPhotoToNote(noteId, eventId, file, caption);
        
        console.log('✅ JobNotesStore: Photo upload completed, refreshing notes');
        
        // Refresh notes via WebSocket
        get().loadEventNotes(eventId);
        
      } catch (error) {
        console.error('❌ JobNotesStore: Photo upload failed:', error.message);
        throw error;
      } finally {
        set({ fileUploading: false });
      }
    },
    
    /**
     * Upload multiple photos to existing note
     */
    uploadMultiplePhotosToNote: async (noteId, eventId, files, captions = []) => {
      console.log('📁 JobNotesStore: Uploading multiple photos:', files.length, 'files');
      
      try {
        set({
          multipleFileUploading: true,
          uploadProgress: { current: 0, total: files.length }
        });
        
        const result = await FileUploadService.uploadMultiplePhotos(noteId, eventId, files, captions);
        
        // Handle partial failures
        if (result.data?.failed_uploads > 0) {
          const successCount = result.data.successful_uploads;
          const failCount = result.data.failed_uploads;
          console.warn('⚠️ JobNotesStore: Partial upload success:', successCount, 'succeeded,', failCount, 'failed');
        }
        
        console.log('✅ JobNotesStore: Batch photo upload completed, refreshing notes');
        
        // Refresh notes via WebSocket
        get().loadEventNotes(eventId);
        
        return result;
        
      } catch (error) {
        console.error('❌ JobNotesStore: Batch photo upload failed:', error.message);
        throw error;
      } finally {
        set({ 
          multipleFileUploading: false,
          uploadProgress: null 
        });
      }
    },
    
    /**
     * Delete photo from note
     */
    deletePhotoFromNote: async (photoId, eventId) => {
      console.log('🗑️ JobNotesStore: Deleting photo:', photoId);
      
      try {
        set({
          fileDeleting: true
        });
        
        const deleteResult = await FileUploadService.deletePhoto(photoId);
        
        if (!deleteResult.success) {
          throw new Error(deleteResult.error);
        }
        
        console.log('✅ JobNotesStore: Photo deleted, refreshing notes');
        
        // Refresh notes via WebSocket
        get().loadEventNotes(eventId);
        
      } catch (error) {
        console.error('❌ JobNotesStore: Photo deletion failed:', error.message);
        throw error;
      } finally {
        set({ fileDeleting: false });
      }
    },
    
    // ================================
    // UTILITY FUNCTIONS (from JobNotesProvider)
    // ================================

    getNotesForEvent: (eventId) => {
      const { notesByEvent } = get();
      return notesByEvent[eventId] || [];
    },
    
    getNoteById: (noteId) => {
      const { notesByEvent } = get();
      // Search through all events for the note
      for (const eventNotes of Object.values(notesByEvent)) {
        const note = eventNotes.find(n => n.id === noteId);
        if (note) return note;
      }
      return null;
    },
    
    getNotesCountForEvent: (eventId) => {
      const { notesByEvent } = get();
      return notesByEvent[eventId]?.length || 0;
    },
    
    // File utility functions
    getPhotoUrl: (filename) => {
      return FileUploadService.getPhotoUrl(filename);
    },
    
    validateFiles: (files) => {
      const results = {
        valid: true,
        errors: [],
        validFiles: [],
        invalidFiles: []
      };

      if (!files || files.length === 0) {
        results.valid = false;
        results.errors.push('No files provided');
        return results;
      }

      // Validate each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validation = FileUploadService.validateFile(file);

        if (validation.valid) {
          results.validFiles.push({
            file,
            index: i,
            info: validation.fileInfo
          });
        } else {
          results.invalidFiles.push({
            file,
            index: i,
            errors: validation.errors
          });
          results.errors.push(`${file.name}: ${validation.errors.join(', ')}`);
        }
      }

      results.valid = results.invalidFiles.length === 0;
      return results;
    },
    
    formatFileSize: (bytes) => {
      return FileUploadService.formatFileSize(bytes);
    },
    
    getPhotoCountForEvent: (eventId) => {
      const { notesByEvent } = get();
      const notes = notesByEvent[eventId] || [];
      return notes.reduce((total, note) => total + (note.photos?.length || 0), 0);
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setNotesLoaded: (data) => {
      console.log('📡 JobNotesStore: Notes loaded for event:', data.event_id);
      
      set(state => ({
        notesByEvent: {
          ...state.notesByEvent,
          [data.event_id]: data.notes || []
        },
        notesLoading: false,
        cache: {
          ...state.cache,
          notesByEvent: {
            ...state.cache.notesByEvent,
            [data.event_id]: data.notes || []
          },
          lastFetch: new Date()
        }
      }));
    },
    
    _addNote: (noteData) => {
      console.log('📡 JobNotesStore: New note created:', noteData.id);
      
      set(state => {
        const eventId = noteData.event_id;
        const updatedNotesByEvent = {
          ...state.notesByEvent,
          [eventId]: [noteData, ...(state.notesByEvent[eventId] || [])]
        };
        
        return {
          notesByEvent: updatedNotesByEvent,
          recentNotes: [noteData, ...state.recentNotes.slice(0, 19)], // Keep last 20
          createNoteLoading: false,
          cache: {
            ...state.cache,
            notesByEvent: updatedNotesByEvent,
            recentNotes: [noteData, ...state.cache.recentNotes.slice(0, 19)]
          }
        };
      });
    },
    
    _updateNote: (noteData) => {
      console.log('📡 JobNotesStore: Note updated:', noteData.id);
      
      set(state => {
        const eventId = noteData.event_id;
        const updatedNotesByEvent = {
          ...state.notesByEvent,
          [eventId]: (state.notesByEvent[eventId] || []).map(note => 
            note.id === noteData.id ? { ...note, ...noteData } : note
          )
        };
        
        // Update selected note if it matches
        const updatedSelectedNote = state.selectedNote?.id === noteData.id 
          ? { ...state.selectedNote, ...noteData }
          : state.selectedNote;
        
        return {
          notesByEvent: updatedNotesByEvent,
          selectedNote: updatedSelectedNote,
          updateNoteLoading: false,
          cache: {
            ...state.cache,
            notesByEvent: updatedNotesByEvent,
            selectedNote: updatedSelectedNote
          }
        };
      });
    },
    
    _removeNote: (data) => {
      console.log('📡 JobNotesStore: Note deleted:', data.note_id);
      
      set(state => {
        // Remove from all events
        const updatedNotesByEvent = { ...state.notesByEvent };
        Object.keys(updatedNotesByEvent).forEach(eventId => {
          updatedNotesByEvent[eventId] = updatedNotesByEvent[eventId].filter(
            note => note.id !== data.note_id
          );
        });
        
        // Remove from recent notes
        const updatedRecentNotes = state.recentNotes.filter(note => note.id !== data.note_id);
        
        // Clear selected note if it was deleted
        const updatedSelectedNote = state.selectedNote?.id === data.note_id 
          ? null 
          : state.selectedNote;
        
        return {
          notesByEvent: updatedNotesByEvent,
          recentNotes: updatedRecentNotes,
          selectedNote: updatedSelectedNote,
          deleteNoteLoading: false,
          cache: {
            ...state.cache,
            notesByEvent: updatedNotesByEvent,
            recentNotes: updatedRecentNotes,
            selectedNote: updatedSelectedNote
          }
        };
      });
    },
    
    // ================================
    // INTERNAL HELPER METHODS
    // ================================
    
    _setupOneTimeNoteCreatedListener: (callback) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        throw new Error('Socket manager not available');
      }
      
      const wrappedCallback = (responseData) => {
        const noteData = responseData.note ? responseData.note : responseData.data || responseData;
        callback(noteData);
      };
      
      socketManager.universalHandler.on(SOCKET_EVENTS.JOB_NOTE_CREATED, wrappedCallback);
      
      // Return cleanup function
      return () => {
        socketManager.universalHandler.off?.(SOCKET_EVENTS.JOB_NOTE_CREATED, wrappedCallback);
      };
    },
    
    // ================================
    // INTERNAL METHODS (socket manager, initialization)
    // ================================
    
    _getSocketManager: () => {
      // Access socket manager from window global
      return { 
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null 
      };
    },
    
    _initialize: (socketManager) => {
      console.log('🔌 JobNotesStore: Initializing with socket manager');
      
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }
      
      set({ 
        isConnected: true,
        _initialized: true 
      });
      
      // No auto-load for job notes (they're loaded per-event basis)
    },
    
    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },
    
    _handleAuthReset: () => {
      console.log('🧹 JobNotesStore: Resetting all state due to auth reset');
      
      set({
        // Reset all state to initial values
        notesByEvent: {},
        selectedNote: null,
        recentNotes: [],
        notesLoading: false,
        createNoteLoading: false,
        updateNoteLoading: false,
        deleteNoteLoading: false,
        fileUploading: false,
        multipleFileUploading: false,
        fileDeleting: false,
        uploadProgress: null,
        isConnected: false,
        cache: {
          notesByEvent: {},
          selectedNote: null,
          recentNotes: [],
          lastFetch: null
        },
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP (Global initialization)
// ================================


export const initializeJobNotesStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 JobNotesStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for all job notes events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useJobNotesStore.getState();
    
    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.JOB_NOTES_LOADED:
        // Extract notes data from response structure
        const data = responseData.notes ? responseData : responseData.data || responseData;
        store._setNotesLoaded(data);
        break;
        
      case SOCKET_EVENTS.JOB_NOTE_CREATED:
        // Handle different possible response structures
        const noteData = responseData.note ? responseData.note : responseData.data || responseData;
        store._addNote(noteData);
        break;
        
      case SOCKET_EVENTS.JOB_NOTE_UPDATED:
        const updatedNoteData = responseData.note ? responseData.note : responseData.data || responseData;
        store._updateNote(updatedNoteData);
        break;
        
      case SOCKET_EVENTS.JOB_NOTE_DELETED:
        const deleteData = responseData.data || responseData;
        store._removeNote(deleteData);
        break;
        
      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_JOB_NOTES_FAILED:
        set({ notesLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_JOB_NOTE_FAILED:
        set({ createNoteLoading: false });
        break;

      case SOCKET_EVENTS.UPDATE_JOB_NOTE_FAILED:
        set({ updateNoteLoading: false });
        break;

      case SOCKET_EVENTS.DELETE_JOB_NOTE_FAILED:
        set({ deleteNoteLoading: false });
        break;

      case 'create_note_with_photos_failed':
        set({ createNoteLoading: false, fileUploading: false });
        console.log('❌ JobNotesStore: Operation failed -', responseData.message);
        break;
        
      default:
        // Not a job notes-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners using existing system
  const jobNotesEvents = [
    // Success events
    SOCKET_EVENTS.JOB_NOTES_LOADED,
    SOCKET_EVENTS.JOB_NOTE_CREATED,
    SOCKET_EVENTS.JOB_NOTE_UPDATED,
    SOCKET_EVENTS.JOB_NOTE_DELETED,
    // Error events
    SOCKET_EVENTS.GET_JOB_NOTES_FAILED,
    SOCKET_EVENTS.CREATE_JOB_NOTE_FAILED,
    SOCKET_EVENTS.UPDATE_JOB_NOTE_FAILED,
    SOCKET_EVENTS.DELETE_JOB_NOTE_FAILED,
    // Custom error events
    'create_note_with_photos_failed'
  ];
  
  // Set up individual event listeners using existing system
  jobNotesEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useJobNotesStore.getState()._handleAuthReset();
    });
  }
  
  // Initialize the store
  useJobNotesStore.getState()._initialize(socketManager);
  
  console.log('✅ JobNotesStore: Socket event listeners initialized');
};

export default useJobNotesStore;